import {
  DownloadSimpleIcon,
  FolderIcon,
  PencilSimpleIcon,
  SlideshowIcon,
} from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { getGalleryTreeApiSessionsGalleryTreeGetOptions } from "../../generated/api/@tanstack/react-query.gen";
import type {
  GalleryTreeResponse,
  HydratedImage,
} from "../../generated/api/types.gen";
import { useImageDownload } from "../../hooks/useImageDownload";
import { useInlineRename } from "../../hooks/useInlineRename";
import { useRenameSession } from "../../hooks/useRenameSession";
import { selectProjectPath } from "../../lib/gallery-navigation";
import { Button } from "../Button";
import { DownloadDropdown } from "../DownloadDropdown";
import { ErrorToast } from "../ErrorToast";
import { GoogleLensButton } from "../GoogleLensButton";
import { getDesignTitle } from "../gallery/galleryTree";
import { SaveAsAssetModal } from "./SaveAsAssetModal";

function projectNameFromTree(
  projectId: string | null | undefined,
  tree: GalleryTreeResponse | undefined,
) {
  if (!projectId) return;
  return tree?.projects?.find((project) => project.id === projectId)?.name;
}

function designTitleFromTree(
  image: HydratedImage,
  tree: GalleryTreeResponse | undefined,
) {
  const session = tree?.sessions?.find(
    (item) => item.session_id === image.session_id,
  );
  return session ? getDesignTitle(session) : image.design_subject;
}

function ProjectChip({ projectId, name }: { projectId: string; name: string }) {
  return (
    <Link
      {...selectProjectPath(projectId)}
      className="inline-flex items-center gap-1 rounded-2xl border border-[var(--accent-primary)]/40 bg-[var(--accent-primary)]/40 px-3 py-1 text-[13px] font-medium text-white no-underline transition-colors hover:bg-[var(--accent-primary)]/60"
    >
      <FolderIcon size={12} aria-hidden />
      {name}
    </Link>
  );
}

function DesignName({
  sessionId,
  title,
}: {
  sessionId: string;
  title: string;
}) {
  const onRename = useRenameSession();
  const {
    isEditing,
    draftName,
    setDraftName,
    startEditing,
    saveName,
    handleNameKeyDown,
    focusOnMount,
  } = useInlineRename(sessionId, title, onRename);

  if (isEditing) {
    return (
      <input
        ref={focusOnMount}
        value={draftName}
        onChange={(event) => setDraftName(event.target.value)}
        onBlur={saveName}
        onKeyDown={handleNameKeyDown}
        aria-label="Design name"
        className="min-w-0 max-w-xs rounded bg-transparent px-1 text-heading-modal outline-none ring-1 ring-[var(--accent-primary)]"
      />
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      <h1
        className="truncate text-heading-modal"
        title="Double-click to rename"
        onDoubleClick={startEditing}
      >
        {title}
      </h1>
      <button
        type="button"
        onClick={startEditing}
        aria-label="Rename design"
        className="shrink-0 text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
      >
        <PencilSimpleIcon size={14} aria-hidden />
      </button>
    </div>
  );
}

export function RefineTopBar({ image }: { image: HydratedImage }) {
  const { data: tree } = useQuery(
    getGalleryTreeApiSessionsGalleryTreeGetOptions(),
  );
  const projectName = projectNameFromTree(image.project_id, tree);
  const title = designTitleFromTree(image, tree);
  const { handleDownload, isDownloading, downloadError, dismissDownloadError } =
    useImageDownload(image);
  const [isSavingAsAsset, setIsSavingAsAsset] = useState(false);

  return (
    <div className="flex w-full shrink-0 flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3">
        <DesignName sessionId={image.session_id} title={title} />
        {projectName && image.project_id && (
          <ProjectChip projectId={image.project_id} name={projectName} />
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button variant="toolbar" onClick={() => setIsSavingAsAsset(true)}>
          <span>Save as Asset</span>
          <SlideshowIcon size={16} aria-hidden />
        </Button>
        <GoogleLensButton imageUrl={image.url || ""} />
        <DownloadDropdown
          onSelect={handleDownload}
          isDownloading={isDownloading}
          label="Download"
          variant="toolbar"
          icon={<DownloadSimpleIcon size={16} aria-hidden />}
          menuDirection="down"
        />
      </div>

      {downloadError && (
        <ErrorToast message={downloadError} onDismiss={dismissDownloadError} />
      )}

      <SaveAsAssetModal
        isOpen={isSavingAsAsset}
        image={image}
        onClose={() => setIsSavingAsAsset(false)}
      />
    </div>
  );
}
